import { createApi } from "@reduxjs/toolkit/query/react";
import type { IAssignedEmployee } from "@/types/(company)/earnings.interface";

import type { IApiResponse } from "@/types";
import type {
  ICreateEarningRequest,
  IEarning,
  IGetEarningByIdRequest,
  IUpdateEarningRequest,
  TGetEarningsListingRequest,
  TGetEarningsListingResponse,
  TSingleEarningResponse,
} from "@/types/(company)/earnings.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyEarningsApi = createApi({
  reducerPath: "companyEarningsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["earnings"],
  endpoints: (builder) => ({
    createEarning: builder.mutation<
      TSingleEarningResponse,
      ICreateEarningRequest
    >({
      query: (body) => {
        return {
          url: "/company/settings/earnings",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["earnings"],
    }),
    getEarnings: builder.query<
      TGetEarningsListingResponse,
      TGetEarningsListingRequest
    >({
      query: (params) => ({
        url: "/company/settings/earnings",
        method: "GET",
        params: params ?? undefined,
      }),
      providesTags: ["earnings"],
    }),
    /**
     * Active earnings only, unpaginated — the shape a dropdown needs.
     *
     * Gated on `settings` for payroll OR time-attendance, unlike the
     * paginated list above which is payroll-only. The overtime policy form
     * lives under Time & Attendance and has to list earnings to map one.
     */
    getActiveEarnings: builder.query<IApiResponse<IEarning[]>, void>({
      query: () => ({
        url: "/company/settings/earnings/active",
        method: "GET",
      }),
      providesTags: ["earnings"],
    }),
    /**
     * The employees paid through a component — the avatar stack's dialog.
     *
     * Separate from the list because a core component like Basic Salary is on
     * every employee in the company: the list carries only a three-person
     * preview, and this is fetched when the stack is actually opened.
     */
    getEarningAssignedEmployees: builder.query<
      IApiResponse<IAssignedEmployee[]>,
      { id: number; page?: number; limit?: number; branchId?: number }
    >({
      query: ({ id, page, limit, branchId }) => ({
        url: `/company/settings/earnings/${id}/assigned-employees`,
        method: "GET",
        // `branchId` omitted rather than sent as undefined: the API reads an
        // absent value as "every branch", which is what the filter's All means.
        params: { page, limit, ...(branchId ? { branchId } : {}) },
      }),
      providesTags: ["earnings"],
    }),
    getEarningById: builder.query<
      TSingleEarningResponse,
      IGetEarningByIdRequest
    >({
      query: (param) => {
        return {
          url: "/company/settings/earnings" + `/${param.id}`,
          method: "GET",
        };
      },
      providesTags: ["earnings"],
    }),
    updateEarning: builder.mutation<
      TSingleEarningResponse,
      IUpdateEarningRequest
    >({
      query: ({ id, ...body }) => {
        return {
          url: "/company/settings/earnings" + `/${id}`,
          method: "PATCH",
          body: body.data,
        };
      },
      invalidatesTags: ["earnings"],
    }),
    deleteEarning: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: "/company/settings/earnings" + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["earnings"],
    }),
  }),
});

export const {
  useGetEarningsQuery,
  useGetActiveEarningsQuery,
  useCreateEarningMutation,
  useUpdateEarningMutation,
  useDeleteEarningMutation,
  useGetEarningByIdQuery,
  useGetEarningAssignedEmployeesQuery,
} = companyEarningsApi;
